import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUICK = ["How long does shipping take?", "How do I choose my size?", "How do I track my order?", "What materials do you use?"];

function sessionId() {
  let id = localStorage.getItem("sync_chat_session");
  if (!id) {
    id = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("sync_chat_session", id);
  }
  return id;
}

export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    fetch(`${API}/assistant/history/${sessionId()}`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d) && d.length) setMessages(d.map((m) => ({ role: m.role, content: m.content }))); })
      .catch(() => {});
  }, [open]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const ev = JSON.parse(payload);
            const chunk = ev.t || (ev.error ? ev.error : "");
            if (chunk) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
                return copy;
              });
            }
          } catch { /* partial line */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't respond just now. Please try again, or email info@thesyncwear.com." };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="chat-open-btn" aria-label="SYNC assistant"
        className="fixed bottom-5 right-5 z-[75] w-14 h-14 bg-chalk text-ink hover:bg-signal transition-colors flex items-center justify-center shadow-xl">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.25 }} data-testid="chat-panel"
            className="fixed bottom-24 right-4 sm:right-5 z-[75] w-[calc(100vw-2rem)] sm:w-[380px] h-[480px] max-h-[70vh] bg-ink border border-border flex flex-col shadow-2xl">
            <div className="px-5 h-16 border-b border-border flex items-center justify-between shrink-0">
              <div>
                <p className="font-anton uppercase text-lg leading-none">SYNC Assistant</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-graphite mt-1">Products · Sizing · Shipping · Orders</p>
              </div>
              <span className="w-2 h-2 rounded-full bg-signal animate-pulse" />
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && (
                <div>
                  <p className="text-graphite text-sm leading-relaxed mb-4">
                    Hey — I'm the SYNC assistant. Ask me about products, sizing, materials, production &amp; shipping times, returns or order tracking.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK.map((q) => (
                      <button key={q} onClick={() => send(q)} data-testid="chat-quick-question"
                        className="font-mono text-[10px] uppercase tracking-wider border border-border text-chalk/70 hover:border-signal hover:text-signal px-3 py-1.5 transition-colors text-left">
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-message-${m.role}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-chalk text-ink" : "border border-border text-chalk/90"}`}>
                    {m.content || <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 bg-graphite rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-graphite rounded-full animate-bounce [animation-delay:120ms]" /><span className="w-1.5 h-1.5 bg-graphite rounded-full animate-bounce [animation-delay:240ms]" /></span>}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-border p-3 flex gap-2 shrink-0">
              <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input"
                placeholder="Ask about sizing, shipping…" maxLength={1000}
                className="flex-1 bg-transparent border border-border px-3 py-2.5 text-sm outline-none focus:border-signal" />
              <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-btn" aria-label="Send"
                className="w-11 bg-chalk text-ink hover:bg-signal transition-colors disabled:opacity-50 flex items-center justify-center">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
